import { useTranslation } from "react-i18next";
import { useState } from "react";
import { captureException, captureMessage, addBreadcrumb, setContext } from "@/lib/sentry";

/**
 * Sentry Test Page
 * 
 * This page allows you to manually trigger different types of errors
 * to verify Sentry is capturing them and sending to Discord.
 */
export default function SentryTest() {
  const [testResult, setTestResult] = useState<string>("");

  const testUnhandledError = () => {
  const { t } = useTranslation();

    addBreadcrumb({
      message: "User clicked 'Test Unhandled Error' button",
      category: "user-action",
      level: "info"
    });
    
    // This will be caught by ErrorBoundary (demonstrates global error catching)
    throw new Error("Test unhandled React error - caught by ErrorBoundary");
  };

  const testCaughtException = () => {
    setTestResult("Sending caught exception to Sentry...");
    addBreadcrumb({
      message: "User clicked 'Test Caught Exception' button",
      category: "user-action",
      level: "info"
    });

    try {
      throw new Error("Test caught exception - simulating API failure");
    } catch (error) {
      captureException(error, {
        tags: {
          test_type: "manual",
          page: "sentry-test",
          action: "test-caught-exception"
        },
        extra: {
          timestamp: new Date().toISOString(),
          userAction: "button_click",
          testScenario: "caught_exception"
        }
      });
      setTestResult("✅ Caught exception sent to Sentry! Check your Discord.");
    }
  };

  const testAuthError = () => {
    setTestResult("Sending auth error to Sentry...");
    addBreadcrumb({
      message: "Simulating authentication failure",
      category: "auth",
      level: "warning"
    });

    const error = new Error("Invalid credentials");
    captureException(error, {
      tags: {
        test_type: "manual",
        page: "sentry-test",
        action: "simulated-auth-error",
        error_type: "authentication"
      },
      extra: {
        email: "test@example.com",
        authProvider: "password",
        attemptCount: 3
      }
    });
    setTestResult("✅ Auth error sent to Sentry! Check your Discord.");
  };

  const testPaymentError = () => {
    setTestResult("Sending payment error to Sentry...");
    addBreadcrumb({
      message: "Simulating payment failure",
      category: "payment",
      level: "error"
    });

    setContext("payment", {
      provider: "stripe",
      amount: 999,
      currency: "usd",
      productId: "test_product_123"
    });

    const error = new Error("Payment processing failed - card declined");
    captureException(error, {
      tags: {
        test_type: "manual",
        page: "sentry-test",
        action: "simulated-payment-error",
        error_type: "payment",
        payment_provider: "stripe"
      },
      extra: {
        cardLast4: "4242",
        checkoutSessionId: "cs_test_123",
        errorCode: "card_declined"
      }
    });
    setTestResult("✅ Payment error sent to Sentry! Check your Discord.");
  };

  const testWarningMessage = () => {
    setTestResult("Sending warning message to Sentry...");
    addBreadcrumb({
      message: "User triggered warning test",
      category: "user-action",
      level: "info"
    });

    captureMessage("Test warning: This is a non-error message", {
      level: "warning",
      tags: {
        test_type: "manual",
        page: "sentry-test",
        message_type: "warning"
      },
      extra: {
        reason: "manual_test",
        timestamp: new Date().toISOString()
      }
    });
    setTestResult("✅ Warning message sent to Sentry! Check your Discord.");
  };

  const testInfoMessage = () => {
    setTestResult("Sending info message to Sentry...");
    
    captureMessage("Test info: Sentry integration working correctly", {
      level: "info",
      tags: {
        test_type: "manual",
        page: "sentry-test",
        message_type: "info"
      }
    });
    setTestResult("✅ Info message sent to Sentry! Check your Discord.");
  };

  const testWithBreadcrumbs = () => {
    setTestResult("Sending error with breadcrumb trail...");
    
    // Create a trail of breadcrumbs
    addBreadcrumb({
      message: "User navigated to test page",
      category: "navigation",
      level: "info"
    });
    
    addBreadcrumb({
      message: "User clicked button",
      category: "ui.click",
      level: "info",
      data: { buttonId: "test-breadcrumbs" }
    });
    
    addBreadcrumb({
      message: "Processing data",
      category: "processing",
      level: "info",
      data: { step: 1, total: 3 }
    });
    
    addBreadcrumb({
      message: "Error occurred during processing",
      category: "processing",
      level: "error",
      data: { step: 2, total: 3 }
    });

    const error = new Error("Test error with breadcrumb trail");
    captureException(error, {
      tags: {
        test_type: "manual",
        page: "sentry-test",
        action: "test-with-breadcrumbs"
      }
    });
    setTestResult("✅ Error with breadcrumbs sent! Check your Discord - you should see the breadcrumb trail.");
  };

  return (
    <div style={{ padding: "40px", maxWidth: "800px", margin: "0 auto" }}>
      <h1 style={{ marginBottom: "20px" }}>🧪 Sentry Integration Test</h1>
      
      <div style={{ 
        background: "#f0f0f0", 
        padding: "20px", 
        borderRadius: "8px", 
        marginBottom: "30px" 
      }}>
        <p style={{ margin: 0, lineHeight: "1.6" }}>
          <strong>{t('ui.text.purpose')}</strong>{t('ui.text.test_that_errors_are_being_captured_by_sentry_and_')}<br />
          <strong>{t('ui.text.expected')}</strong>{t('ui.text.each_button_click_should_send_an_event_to_sentry_t')}<br />
          <strong>{t('ui.text.dashboard')}</strong> <a href="https://sentry.io/organizations/aaronmichaelfrost/issues/" target="_blank" rel="noopener noreferrer">{t('ui.text.view_sentry_issues')}</a>
        </p>
      </div>

      {testResult && (
        <div style={{
          background: testResult.includes("✅") ? "#d4edda" : "#fff3cd",
          border: `1px solid ${testResult.includes("✅") ? "#c3e6cb" : "#ffeaa7"}`,
          color: testResult.includes("✅") ? "#155724" : "#856404",
          padding: "15px",
          borderRadius: "4px",
          marginBottom: "20px"
        }}>
          {testResult}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
        <div>
          <h3 style={{ marginBottom: "10px" }}>{t('ui.text.manual_tracking_try_catch')}</h3>
          <p style={{ fontSize: "14px", color: "#666", marginBottom: "10px" }}>{t('ui.text.these_simulate_errors_inside_try_catch_blocks_that')}<code>{t('ui.text.capture_exception')}</code>
          </p>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button 
              onClick={testCaughtException}
              style={buttonStyle("#007bff")}
            >{t('ui.text.test_caught_exception')}</button>
            <button 
              onClick={testAuthError}
              style={buttonStyle("#ffc107")}
            >{t('ui.text.test_auth_error')}</button>
            <button 
              onClick={testPaymentError}
              style={buttonStyle("#dc3545")}
            >{t('ui.text.test_payment_error')}</button>
            <button 
              onClick={testWithBreadcrumbs}
              style={buttonStyle("#6f42c1")}
            >{t('ui.text.test_with_breadcrumbs')}</button>
          </div>
        </div>

        <div>
          <h3 style={{ marginBottom: "10px" }}>{t('ui.text.messages_non_error_events')}</h3>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button 
              onClick={testWarningMessage}
              style={buttonStyle("#fd7e14")}
            >{t('ui.text.test_warning_message')}</button>
            <button 
              onClick={testInfoMessage}
              style={buttonStyle("#17a2b8")}
            >{t('ui.text.test_info_message')}</button>
          </div>
        </div>

        <div>
          <h3 style={{ marginBottom: "10px" }}>{t('ui.text.global_error_boundary')}</h3>
          <p style={{ fontSize: "14px", color: "#666", marginBottom: "10px" }}>{t('ui.text.this_simulates_an_unhandled_react_error_that_bubbl')}</p>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button 
              onClick={testUnhandledError}
              style={buttonStyle("#dc3545")}
            >
              ⚠️ Test Unhandled Error (Triggers ErrorBoundary)
            </button>
          </div>
          <p style={{ fontSize: "14px", color: "#dc3545", marginTop: "5px", fontWeight: "bold" }}>
            ⚠️ This will crash the page and show the ErrorBoundary fallback UI. Click "Try again" to recover.
          </p>
        </div>
      </div>

      <div style={{ 
        marginTop: "40px", 
        padding: "20px", 
        background: "#e7f3ff", 
        borderRadius: "8px",
        borderLeft: "4px solid #007bff"
      }}>
        <h3 style={{ marginTop: 0 }}>💡 Testing Tips</h3>
        <ul style={{ lineHeight: "1.8" }}>
          <li><strong>{t('ui.text.manual_tracking')}</strong>{t('ui.text.use_test_caught_exception_first_demonstrates_error')}</li>
          <li><strong>{t('ui.text.errorboundary')}</strong> {t('ui.text.test_unhandled_error_desc')}</li>
          <li>{t('ui.text.each_error_includes_unique_tags')}<code>{t('ui.text.test_type_manual')}</code>{t('ui.text.so_you_can_identify_them_in_sentry')}</li>
          <li>{t('ui.text.check_your_discord_channel_within_30_seconds_of_cl')}</li>
          <li>{t('ui.text.in_sentry_dashboard_compare_the_two_types_manual_e')}</li>
          <li>{t('ui.text.the_test_with_breadcrumbs_shows_how_user_actions_a')}</li>
        </ul>
      </div>
      
      <div style={{ 
        marginTop: "20px", 
        padding: "20px", 
        background: "#fff3cd", 
        borderRadius: "8px",
        borderLeft: "4px solid #ffc107"
      }}>
        <h3 style={{ marginTop: 0 }}>🔍 What to Look For</h3>
        <p style={{ margin: 0, lineHeight: "1.8" }}>
          <strong>{t('ui.text.manual_tracking_section_1')}</strong>{t('ui.text.these_stay_on_the_page_show_a_success_message_and_')}<code>{t('ui.text.page_sentry_test')}</code> and <code>{t('ui.text.action_test_caught_exception')}</code><br/>
          <strong>{t('ui.text.errorboundary_section_2')}</strong>{t('ui.text.this_crashes_the_page_shows_a_fallback_ui_and_incl')}</p>
      </div>
    </div>
  );
}

const buttonStyle = (color: string) => ({
  padding: "12px 24px",
  background: color,
  color: "white",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "500" as const,
  transition: "opacity 0.2s",
  ":hover": {
    opacity: 0.9
  }
});
